import * as moment from 'moment';
import { groupBy } from 'lodash';
import { ICalendarOptions, ICalendar, IDay, IWeek, IDateRange, Weekday } from '../classes/calendar';
import { IEvent } from '../custom/classes/event';

/**
 * Returns an instance of ICalendar
 */
export function create<T extends IEvent>(options: ICalendarOptions<T>): ICalendar<T> {
    const days = calendarDays(options);
    const allDays = eventDays(options);
    return {
        days,
        allDays,
        weeks: weeks(days),
        weekdays: weekdays(options)
    };
}

/**
 * Fixes behavior where negatiave dividends do not
 * "wrap back around". For example, we want the following
 * behavior: -3 % 7 = 4.
 */
export function mod(dividend: number, divisor: number): number {
    return ((dividend % divisor) + divisor) % divisor;
}


/**
 * Returns an array of Dates for the given calendar month. Accepts
 * year, month, and an optional weekStart option.
 */
export function calendarDays<T extends IEvent>(options: ICalendarOptions<T>): Array<IDay<T>> {
    let range = calendarDateRange(options);
    let days = interpolateDays(range).map((d) => { return { date: d } });

    if (options.events) {
        return addEvents(days, options.events);
    }

    return days;
}

/**
 * Returns an array of Dates for the given calendar month. Accepts
 * year, month, and an optional weekStart option.
 */
export function eventDays<T extends IEvent>(options: ICalendarOptions<T>): Array<IDay<T>> {
    if (options.events.length > 0) {
        let range = { start: moment(options.events[0].eventDate).toDate(), end: moment(options.events[options.events.length - 1].eventDate).toDate() };
        let days = interpolateDays(range).map((d) => { return { date: d } });

        if (options.events) {
            return addEvents(days, options.events);
        }
        return days;
    } else {
        return [];
    }
}


/**
 * Converts an Array of days to an Array of weeks.
 */
export function weeks<T extends IEvent>(days: Array<IDay<T>>): Array<IWeek<T>> {
    return days.reduce(function (weeks, day, idx) {
        let weekIdx = Math.floor(idx / 7);
        if (!weeks[weekIdx]) weeks.push([]);

        weeks[weekIdx].push(day);
        return weeks;
    }, []);
}


/**
 * Returns start and end dates for the given calendar month. Accepts
 * year, month, and an optional weekStart option.
 */
export function calendarDateRange<T extends IEvent>({ year, month, weekStart }: ICalendarOptions<T>): IDateRange {
    // Default weekStart to Sunday
    if (typeof weekStart !== 'number' || isNaN(weekStart)) {
        weekStart = Weekday.Sunday;
    }

    if (weekStart > 6 || weekStart < 0) {
        throw new Error('weekStart must be an integer in the range [0, 6]');
    }

    let monthStart = moment([year, month, 1]);
    let start = monthStart.clone();

    // Subtract start offset if necessary; this will occur
    // if the first day of the month does not coincide with
    // the first day of the week.
    let startOffset = mod(start.day() - weekStart, 7);

    if (startOffset != 0) {
        start.subtract(startOffset, 'days');
    }

    // Add an end offset if necessary; this will occur
    // if the last day of the month does not coincide with
    // the last day of the week.
    let end = monthStart.endOf('month');
    let endIndex = mod(end.day() - weekStart, 7);
    let endOffset = 6 - endIndex;

    if (endOffset != 0) {
        end.add(endOffset, 'days');
    }

    return { start: start.toDate(), end: end.toDate() };
}


/**
 * Returns an array of Dates in the inclusive range [start, end].
 */
export function interpolateDays(range: IDateRange): Array<Date> {
    let dates = [];
    let current = moment(range.start).startOf('day');
    let end = moment(range.end).startOf('day');

    if (end < current) {
        return [];
    }

    while (current <= end) {
        dates.push(current.clone().toDate());
        current.add(1, 'days');
    }

    return dates;
}


/**
 * Adds events to each day. The events and days options are required.
 */
export function addEvents<T extends IEvent>(days: Array<IDay<T>>, events: Array<T>): Array<IDay<T>> {

    const groupedEvents = groupBy(events, e => moment(e.eventDate).startOf('day').format('YYYYMMDD'));

    return days.map(d => {
        const date = moment(d.date).startOf('day').format('YYYYMMDD');
        const events = groupedEvents[date];

        d.events = events ? events : [];
        return d;
    })
}


/**
 * Returns a function that evaluates if an event's date
 * is the same as the supplied day.
 */
export function isSameDay<T extends IEvent>(day: IDay<T>) {
    return function (event: T): boolean {
        return moment(event.eventDate).isSame(moment(day.date), 'day');
    }
}


/**
 * Returns a list of weekdays with the appropriate weekday start
 * day listed first. Used to support week formats that have a
 * start day other than Sunday.
 */
export function weekdays<T extends IEvent>({ weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"], weekStart = Weekday.Sunday }): Array<string> {
    if (weekStart > 6 || weekStart < 0) {
        throw new Error('weekStart must be an integer in the range [0, 6]');
    }

    // Simplified since these won't change.
    let weekdayIndices = [0, 1, 2, 3, 4, 5, 6];

    let firstHalf = weekdayIndices.slice(weekStart);
    let secondHalf = weekdayIndices.slice(0, weekStart);

    return firstHalf.concat(secondHalf).map(i => weekdayNames[i]);
}
